import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Mail } from 'lucide-react';
import {
  EMAIL_LIMIT_MAX,
  SWITCHABLE_EMAIL_KINDS,
  type EmailSwitchMap,
  type OwnerTenantEmailDto,
  type OwnerTenantEmailRequest,
  type SwitchableEmailKind,
} from '@his/shared';
import { get, post, put } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { dateTime } from '@/lib/utils';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { Dialog } from '@/components/ui/dialog';
import { Alert, Badge, Button, Checkbox, Field, Input, Switch } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { EmailLogTable, emailKindLabel, quotaLine } from '../admin/EmailSettingsTab';
import { TotpField, isValidTotp } from './OwnerConfirm';
import { useOwnerCapability } from './owner-nav';

const PACK_CHOICES = ['0', '1', '2', '5', '10'] as const;

export const OWNER_EMAIL_PAUSED_KEY = ['owner', 'email', 'paused'] as const;

export function ownerTenantEmailKey(tenantId: string): readonly string[] {
  return ['owner', 'email', 'tenant', tenantId];
}

export function limitFromDraft(draft: string): number | null | 'invalid' {
  const trimmed = draft.trim();
  if (trimmed === '') return null;
  if (!/^\d+$/.test(trimmed)) return 'invalid';
  const value = Number.parseInt(trimmed, 10);
  return value <= EMAIL_LIMIT_MAX ? value : 'invalid';
}

export function tenantEmailChanges(
  view: OwnerTenantEmailDto,
  draft: { daily: string; monthly: string; packs: number; ownerSmtpAllowed: boolean; switches: EmailSwitchMap },
): Omit<OwnerTenantEmailRequest, 'totpCode'> | null {
  const daily = limitFromDraft(draft.daily);
  const monthly = limitFromDraft(draft.monthly);
  if (daily === 'invalid' || monthly === 'invalid') return null;
  const switchesChanged = SWITCHABLE_EMAIL_KINDS.some((kind) => (draft.switches[kind] ?? true) !== (view.switches[kind] ?? true));
  const changes: Omit<OwnerTenantEmailRequest, 'totpCode'> = {
    ...(daily !== view.dailyOverride ? { dailyLimit: daily } : {}),
    ...(monthly !== view.monthlyOverride ? { monthlyLimit: monthly } : {}),
    ...(draft.packs > 0 ? { addPacks: draft.packs } : {}),
    ...(draft.ownerSmtpAllowed !== view.ownerSmtpAllowed ? { ownerSmtpAllowed: draft.ownerSmtpAllowed } : {}),
    ...(switchesChanged ? { switches: draft.switches } : {}),
  };
  return Object.keys(changes).length > 0 ? changes : null;
}

function grouped(count: number): string {
  return count.toLocaleString('en-US');
}

export function EmailQuotaDialog({ tenant, onClose }: { tenant: { id: string; name: string; slug: string }; onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const canUpdate = useOwnerCapability('email.update');
  const canResume = useOwnerCapability('email.resume');
  const [daily, setDaily] = useState<string | null>(null);
  const [monthly, setMonthly] = useState<string | null>(null);
  const [packs, setPacks] = useState<(typeof PACK_CHOICES)[number]>('0');
  const [ownerSmtpAllowed, setOwnerSmtpAllowed] = useState<boolean | null>(null);
  const [switches, setSwitches] = useState<EmailSwitchMap | null>(null);
  const [totpCode, setTotpCode] = useState('');
  const [showLog, setShowLog] = useState(false);

  const query = useQuery({
    queryKey: ownerTenantEmailKey(tenant.id),
    queryFn: () => get<OwnerTenantEmailDto>(`/owner/email/tenants/${tenant.id}`),
    staleTime: 0,
    retry: false,
  });
  const view = query.data;

  const reset = () => {
    setDaily(null);
    setMonthly(null);
    setPacks('0');
    setOwnerSmtpAllowed(null);
    setSwitches(null);
    setTotpCode('');
  };

  const settle = (next: OwnerTenantEmailDto) => {
    queryClient.setQueryData(ownerTenantEmailKey(tenant.id), next);
    void queryClient.invalidateQueries({ queryKey: OWNER_EMAIL_PAUSED_KEY });
    reset();
  };

  const draft = view
    ? {
        daily: daily ?? (view.dailyOverride === null ? '' : String(view.dailyOverride)),
        monthly: monthly ?? (view.monthlyOverride === null ? '' : String(view.monthlyOverride)),
        packs: Number(packs),
        ownerSmtpAllowed: ownerSmtpAllowed ?? view.ownerSmtpAllowed,
        switches: switches ?? view.switches,
      }
    : null;
  const changes = view && draft ? tenantEmailChanges(view, draft) : null;
  const invalid = draft !== null && (limitFromDraft(draft.daily) === 'invalid' || limitFromDraft(draft.monthly) === 'invalid');

  const save = useMutation({
    mutationFn: () => put<OwnerTenantEmailDto>(`/owner/email/tenants/${tenant.id}`, { ...changes, totpCode }),
    onSuccess: (next) => {
      settle(next);
      toast.success(`Email settings of ${tenant.name} saved`);
    },
    onError: (error) => toast.error('Could not save the email settings', describeError(error)),
  });

  const resume = useMutation({
    mutationFn: () => post<OwnerTenantEmailDto>(`/owner/email/tenants/${tenant.id}/resume`, { totpCode }),
    onSuccess: (next) => {
      settle(next);
      toast.success(`Email sending of ${tenant.name} resumed`);
    },
    onError: (error) => toast.error('Could not resume sending', describeError(error)),
  });

  const quota = view?.quota;
  const resumable = Boolean(view && view.pause.paused && (view.ownSmtp || quota?.exhaustedBy === null));

  return (
    <Dialog open onClose={onClose} wide icon={Mail} title={`Email · ${tenant.name}`} description="Mail sent through the platform mail server counts against these limits. A facility with its own mail server is not limited. Sending that paused at a limit never restarts by itself.">
      {query.isLoading || !view || !quota || !draft ? (
        <p className="py-6 text-sm text-muted-foreground">Loading…</p>
      ) : (
        <div className="space-y-4">
          {view.pause.paused ? (
            <Alert variant="warning">
              Paused {dateTime(view.pause.pausedAt)} at the {view.pause.reason === 'monthly_limit' ? 'monthly' : 'daily'} limit. Queued emails are held.
              {!resumable && ' The limit is still used up: raise it or add a pack first.'}
            </Alert>
          ) : (
            <Badge variant="success">Sending</Badge>
          )}

          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-md border p-3">
              <p className="text-xs text-muted-foreground">Today</p>
              <p className="font-mono text-sm">{quotaLine(quota.usedToday, quota.dailyLimit)}</p>
            </div>
            <div className="rounded-md border p-3">
              <p className="text-xs text-muted-foreground">This month</p>
              <p className="font-mono text-sm">{quotaLine(quota.usedThisMonth, quota.monthlyAllowance)}</p>
            </div>
            <div className="rounded-md border p-3">
              <p className="text-xs text-muted-foreground">Extra this month</p>
              <p className="font-mono text-sm">{grouped(quota.extraThisMonth)}</p>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            {view.ownSmtp ? 'Has its own mail server: the platform limits do not apply to it.' : 'Uses the platform mail server when it sends.'}
            {view.deploymentMode === 'LOCAL' && ' Runs on its own server: it sends through its own mail server only.'}
          </p>

          {canUpdate && (
            <div className="grid gap-4 sm:grid-cols-3">
              <Field label="Emails per day" hint={`Empty: platform default (${view.platformDaily > 0 ? grouped(view.platformDaily) : 'no limit'}). 0: no limit.`} htmlFor="email-daily">
                <Input id="email-daily" inputMode="numeric" value={draft.daily} onChange={(e) => setDaily(e.target.value.replace(/\D/g, '').slice(0, 8))} />
              </Field>
              <Field label="Emails per month" hint={`Empty: platform default (${view.platformMonthly > 0 ? grouped(view.platformMonthly) : 'no limit'}). 0: no limit.`} htmlFor="email-monthly">
                <Input id="email-monthly" inputMode="numeric" value={draft.monthly} onChange={(e) => setMonthly(e.target.value.replace(/\D/g, '').slice(0, 8))} />
              </Field>
              <Field label="Add extra emails this month" hint={`One pack is ${grouped(view.packSize)} emails.`} htmlFor="email-packs">
                <ChoiceButtons
                  id="email-packs"
                  layout="chips"
                  value={packs}
                  onChange={setPacks}
                  options={PACK_CHOICES.map((choice) => ({ value: choice, label: choice === '0' ? 'None' : `${choice} × ${grouped(view.packSize)}` }))}
                />
              </Field>
              <div className="sm:col-span-3">
                <Switch
                  checked={draft.ownerSmtpAllowed}
                  onCheckedChange={(checked) => setOwnerSmtpAllowed(checked)}
                  label="Allow the platform mail server"
                  description="Off: this facility sends only through its own mail server."
                />
              </div>
              <div className="space-y-2 sm:col-span-3">
                <p className="text-xs font-medium">Kinds of email this facility may send</p>
                <div className="grid gap-2 sm:grid-cols-3">
                  {SWITCHABLE_EMAIL_KINDS.map((kind: SwitchableEmailKind) => (
                    <Checkbox
                      key={kind}
                      checked={draft.switches[kind] ?? true}
                      onCheckedChange={(checked) => setSwitches({ ...draft.switches, [kind]: checked })}
                      label={emailKindLabel(kind)}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}

          {(canUpdate || (canResume && view.pause.paused)) && <TotpField value={totpCode} onChange={setTotpCode} />}

          <div className="flex flex-wrap gap-2">
            {canUpdate && (
              <Button loading={save.isPending} disabled={!changes || invalid || !isValidTotp(totpCode)} onClick={() => save.mutate()}>
                Save
              </Button>
            )}
            {canResume && view.pause.paused && (
              <Button variant="outline" loading={resume.isPending} disabled={!resumable || !isValidTotp(totpCode)} onClick={() => resume.mutate()}>
                Resume sending
              </Button>
            )}
            <Button variant="ghost" onClick={() => setShowLog(!showLog)}>
              {showLog ? 'Hide email log' : 'Show email log'}
            </Button>
          </div>

          {showLog && <EmailLogTable path={`/owner/email/tenants/${tenant.id}/logs`} queryKey={['owner', 'email', 'logs', tenant.id]} />}
        </div>
      )}
    </Dialog>
  );
}
